/**
 * components/landing/SiteFrame.jsx — shared page frame.
 *
 * Composes SiteHeader + <main> slot + FooterCta + SiteFooter.  Every
 * Kalki Ai Cloud marketing page wraps its content in this frame so header
 * pinning, spacing, and footer CTA are identical everywhere.
 *
 * 2026-07 · Phase 1 · Dark-mode lock
 * ────────────────────────────────────────────────────────────────
 * The tenant workspace supports a per-user light/dark toggle via
 * `<html data-theme="light|dark">` — and `globals.css` runs `!important`
 * overrides on `.bg-black`, `.text-white`, `[class*="bg-zinc-9*"]`
 * etc. when the user chose light mode.  The Kalki Ai Cloud corporate site is
 * ALWAYS dark (per the Phase 1 PRD, the design is "premium dark
 * theme"), so on mount we force `<html data-theme="dark" class="dark">`
 * and roll it back to the user's saved preference on unmount.  This
 * keeps the tenant workspace's light-mode toggle intact for people
 * who navigate back into /hub/*.
 */

import { useEffect } from 'react'
import SiteHeader from './SiteHeader'
import SiteFooter, { FooterCta } from './SiteFooter'

const SiteFrame = ({ children, hideFooterCta = false }) => {
  useEffect(() => {
    const html = document.documentElement
    // Snapshot the current theme so we can restore on unmount when the
    // user navigates out of Kalki Ai Cloud back into /hub/* — the tenant
    // workspace should keep whatever light/dark they had before.
    const prevTheme      = html.getAttribute('data-theme')
    const prevHadDark    = html.classList.contains('dark')

    html.setAttribute('data-theme', 'dark')
    html.classList.add('dark')

    return () => {
      if (prevTheme) html.setAttribute('data-theme', prevTheme)
      else           html.removeAttribute('data-theme')
      if (!prevHadDark) html.classList.remove('dark')
    }
  }, [])

  return (
    <div
      className="min-h-screen antialiased"
      style={{
        backgroundColor: '#000000',
        color:           '#ffffff',
        colorScheme:     'dark',
      }}
    >
      <SiteHeader />
      <main id="brand-main" className="pt-4">
        {children}
        {!hideFooterCta && <FooterCta />}
      </main>
      <SiteFooter />
    </div>
  )
}

export default SiteFrame
